import { Fragment } from "react";
import { Box, Typography } from "@mui/material";
import { Check, Lock, AlertTriangle } from "lucide-react";

import { brand } from "../../../theme/theme";

const SUCCESS = "#0F9D58";

const circleSx = (state) => {
  const base = {
    width: 30,
    height: 30,
    minWidth: 30,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.8125rem",
    fontWeight: 700,
    transition: "box-shadow 0.15s",
  };
  if (state === "done") {
    return { ...base, backgroundColor: "#E3F6EA", color: SUCCESS, border: `1px solid #C4E8D2` };
  }
  if (state === "active") {
    return {
      ...base,
      backgroundColor: brand.inkFill,
      color: brand.inkFillText,
      boxShadow: "0 4px 14px rgba(15, 23, 42, 0.22)",
    };
  }
  if (state === "error") {
    return { ...base, backgroundColor: "#FEE2E2", color: "#DC2626", border: "1px solid #FCA5A5" };
  }
  return { ...base, backgroundColor: "#fff", color: "#64748b", border: "1px solid #CBD5E1" };
};

/**
 * Progress of the content workflow (Generación → Revisión → Programación).
 * Each step can be clicked to open its view. Stacks on small screens.
 */
export function WorkflowStepper({ steps, selectedKey, onSelect, sx = {} }) {
  const isClickable = typeof onSelect === "function";

  return (
    <Box
      component="nav"
      aria-label="Fases del contenido"
      sx={{
        display: { xs: "grid", md: "flex" },
        gridTemplateColumns: { xs: "1fr", sm: `repeat(${steps.length}, minmax(0, 1fr))` },
        alignItems: "center",
        gap: { xs: 1, md: 0 },
        maxWidth: { md: 1440 }, // on wide screens the box stops growing and stays on the left
        px: { xs: 1.5, md: 2.5 },
        py: { xs: 1.5, md: 1.75 },
        borderRadius: "16px",
        border: "1px solid #E8ECF2",
        backgroundColor: "#fff",
        ...sx,
      }}
    >
      {steps.map((step, index) => {
        const selected = step.key === selectedKey;
        const clickable = isClickable && !step.disabled;
        const nextState = steps[index + 1]?.state;
        const lineDone = step.state === "done" && nextState && nextState !== "pending" && nextState !== "blocked";
        return (
          <Fragment key={step.key}>
            <Box
              component={clickable ? "button" : "div"}
              type={clickable ? "button" : undefined}
              onClick={clickable ? () => onSelect(step.key) : undefined}
              aria-current={selected ? "step" : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                minWidth: 0,
                flexShrink: 0,
                p: 0.75,
                pr: 1.25,
                border: "none",
                borderRadius: "12px",
                background: selected ? brand.soft : "transparent",
                cursor: clickable ? "pointer" : "default",
                textAlign: "left",
                font: "inherit",
                transition: "background-color 0.15s",
                "&:hover": clickable ? { backgroundColor: selected ? brand.soft : "#F6F7FA" } : {},
                "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: 2 },
              }}
            >
              <Box sx={circleSx(step.state)}>
                {step.state === "done" ? (
                  <Check size={15} strokeWidth={3} />
                ) : step.state === "blocked" ? (
                  <Lock size={13} />
                ) : step.state === "error" ? (
                  <AlertTriangle size={14} />
                ) : (
                  index + 1
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 700,
                    lineHeight: 1.25,
                    color: step.state === "active" || selected ? brand.ink : "#1F2937",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {step.title}
                </Typography>
                {step.subtitle && (
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      lineHeight: 1.3,
                      color: step.state === "error" ? "#DC2626" : "#6B7280",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {step.subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
            {index < steps.length - 1 && (
              <Box
                aria-hidden
                sx={{
                  display: { xs: "none", md: "block" },
                  flex: 1,
                  minWidth: 24,
                  height: 2,
                  mx: 1.5,
                  borderRadius: 2,
                  backgroundColor: lineDone ? "#86CFA3" : "#E5E7EB",
                  ...(step.state === "done" && nextState === "active"
                    ? { background: `linear-gradient(90deg, #86CFA3, ${brand.ink})` }
                    : {}),
                }}
              />
            )}
          </Fragment>
        );
      })}
    </Box>
  );
}
